import type { Conge } from '@shared/types/Conge';
import type { Salarie } from '@shared/types/Salarie';

export type PilotageAlertGravite = 'URGENT' | 'ATTENTION' | 'INFO';

export interface PilotageAlert {
  type: string;
  salarie_id: string;
  salarie_label: string;
  echeance: string;
  jours: number;
  gravite: PilotageAlertGravite;
  /** Message métier lisible (ex. contrat qui expire). */
  message: string;
}

export interface PilotageKpis {
  effectif: number;
  cddExpiring: number;
  essaiEnding: number;
  visitesMedicales: number;
  sejourExpiring: number;
}

function daysUntil(dateStr: string, ref: Date): number {
  const d = new Date(`${dateStr}T00:00:00`);
  return Math.round((d.getTime() - ref.getTime()) / 86_400_000);
}

function salarieLabel(s: Salarie): string {
  return `${s.prenom} ${s.nom}`;
}

function delaiPhrase(jours: number): string {
  if (jours < 0) return `depuis ${-jours} jour${-jours > 1 ? 's' : ''}`;
  if (jours === 0) return "aujourd'hui";
  return `dans ${jours} jour${jours > 1 ? 's' : ''}`;
}

/** Message type contrat / échéance nominative. */
export function messageEcheanceContrat(
  label: string,
  echeance: string,
  jours: number,
  sujet = 'Contrat',
): string {
  return `${sujet} de ${label} expire le ${echeance} — ${delaiPhrase(jours)}`;
}

function graviteFromJours(jours: number, urgentMax: number, attentionMax: number): PilotageAlertGravite {
  if (jours < 0 || jours <= urgentMax) return 'URGENT';
  if (jours <= attentionMax) return 'ATTENTION';
  return 'INFO';
}

/** Effectif pilotable : hors sortis et hors inactifs (aligné filtre « en poste » de la liste Salariés). */
export function actifsHorsSortis(salaries: readonly Salarie[]): Salarie[] {
  return salaries.filter((s) => s.statut !== 'sortie' && s.statut !== 'inactif');
}

export function buildPilotageAlerts(
  salaries: readonly Salarie[],
  conges: readonly Conge[],
  ref = new Date(),
): PilotageAlert[] {
  const active = actifsHorsSortis(salaries);
  const alerts: PilotageAlert[] = [];

  for (const s of active) {
    const label = salarieLabel(s);

    if (s.statut_contrat === 'CDD' && s.date_fin_contrat) {
      const j = daysUntil(s.date_fin_contrat, ref);
      if (j >= 0 && j <= 60) {
        alerts.push({
          type: 'Fin de CDD',
          salarie_id: s.id,
          salarie_label: label,
          echeance: s.date_fin_contrat,
          jours: j,
          gravite: graviteFromJours(j, 30, 60),
          message: messageEcheanceContrat(label, s.date_fin_contrat, j),
        });
      }
    }

    if (s.essai_fin) {
      const j = daysUntil(s.essai_fin, ref);
      if (j >= 0 && j <= 30) {
        alerts.push({
          type: "Fin période d'essai",
          salarie_id: s.id,
          salarie_label: label,
          echeance: s.essai_fin,
          jours: j,
          gravite: graviteFromJours(j, 7, 30),
          message: messageEcheanceContrat(label, s.essai_fin, j, "Période d'essai"),
        });
      }
    }

    if (s.visite_prochaine) {
      const j = daysUntil(s.visite_prochaine, ref);
      if (j < 0) {
        alerts.push({
          type: 'Visite médicale dépassée',
          salarie_id: s.id,
          salarie_label: label,
          echeance: s.visite_prochaine,
          jours: j,
          gravite: 'URGENT',
          message: `Visite médicale de ${label} dépassée depuis le ${s.visite_prochaine} — ${delaiPhrase(j)}`,
        });
      } else if (j <= 30) {
        alerts.push({
          type: 'Visite médicale à planifier',
          salarie_id: s.id,
          salarie_label: label,
          echeance: s.visite_prochaine,
          jours: j,
          gravite: graviteFromJours(j, 7, 30),
          message: `Visite médicale de ${label} prévue le ${s.visite_prochaine} — ${delaiPhrase(j)}`,
        });
      }
    }

    if (s.sejour_expiration) {
      const j = daysUntil(s.sejour_expiration, ref);
      if (j <= 90) {
        alerts.push({
          type: 'Titre de séjour',
          salarie_id: s.id,
          salarie_label: label,
          echeance: s.sejour_expiration,
          jours: j,
          gravite: graviteFromJours(j, 30, 90),
          message: messageEcheanceContrat(label, s.sejour_expiration, j, 'Titre de séjour'),
        });
      }
    }
  }

  for (const c of conges.filter((x) => x.statut === 'demande')) {
    const sal = active.find((s) => s.id === c.salarie_id);
    if (!sal) continue;
    const j = daysUntil(c.date_debut, ref);
    const label = salarieLabel(sal);
    alerts.push({
      type: 'Congé en attente',
      salarie_id: sal.id,
      salarie_label: label,
      echeance: c.date_debut,
      jours: j,
      gravite: 'INFO',
      message: `Demande de congé de ${label} à partir du ${c.date_debut} — ${delaiPhrase(j)}`,
    });
  }

  return alerts.sort((a, b) => a.jours - b.jours);
}

export function computePilotageKpis(
  salaries: readonly Salarie[],
  alerts: readonly PilotageAlert[],
): PilotageKpis {
  const active = actifsHorsSortis(salaries);
  return {
    effectif: active.length,
    cddExpiring: alerts.filter((a) => a.type === 'Fin de CDD').length,
    essaiEnding: alerts.filter((a) => a.type === "Fin période d'essai").length,
    visitesMedicales: alerts.filter((a) => a.type.startsWith('Visite médicale')).length,
    sejourExpiring: alerts.filter((a) => a.type === 'Titre de séjour').length,
  };
}
